import phrService from '@/apis/phr/service';
import createStore from '@/stores/createStore';
import useGlobalConfigs from '@/stores/globalConfigs';
import _ from 'lodash';
import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { StoreApi, StateSelector, useStore } from 'zustand';

interface IPhrStore {
  uid: string;
  phr?: IPhrData;
  user?: IUser;
  phrObjComponent?: Record<string, IEMRData>;
}

const PhrStoreContext = React.createContext<StoreApi<IPhrStore> | null>(null);

interface PhrStoreProviderProps {
  children: React.ReactNode;
}

const PhrStoreProvider: React.FC<PhrStoreProviderProps> = ({ children }) => {
  const setRefetchPHR = useGlobalConfigs((s) => s.setRefetchPHR);
  const storeRef = React.useRef<StoreApi<IPhrStore>>();
  const params = useParams();
  const uid = params.uid as string;
  const { data, refetch } = phrService.useGetPhr({ uid });

  useEffect(() => {
    setRefetchPHR(refetch);
  }, [refetch, setRefetchPHR]);

  if (!storeRef.current) {
    storeRef.current = createStore<IPhrStore>(() => ({
      uid: params?.uid || '',
      phr: undefined,
      user: undefined,
    }));
  }

  React.useEffect(() => {
    const phr: IPhrData = data?.data;
    if (phr) {
      const objMEmber = [...(phr?.user?.family?.members || []), phr?.user?.family?.protector]
        .filter(Boolean)
        ?.reduce((d: any, u) => {
          d[u?._id] = u;
          return d;
        }, {});

      _.set(
        phr,
        'user.family.relationsWithMe',
        phr.user?.family.relations?.reduce((d: Record<string, IRelation>, r: IRelation) => {
          if (r.source === uid) {
            r.value = r.value?.split(' - ')?.[1];
            r.username = objMEmber?.[r.destination]?.username;
            // console.log(objMEmber);
            d[r.destination] = r;
          }
          return d;
        }, {}),
      );
      const user = phr.user;
      const phrObjComponent: Record<string, IEMRData> = (phr?.components || [])?.reduce((d: any, e: any) => {
        d[e?.type?.code] = e;
        return d;
      }, {});

      storeRef.current?.setState((s) => ({ ...s, uid: user?._id, user, phr, phrObjComponent }));
    }
  }, [data?.data, uid]);

  return <PhrStoreContext.Provider value={storeRef.current}>{children}</PhrStoreContext.Provider>;
};

function usePhrStoreInContext<T = IPhrStore>(selector?: StateSelector<IPhrStore, T>): T {
  const store = React.useContext(PhrStoreContext);

  if (!store) {
    throw new Error('Missing StoreProvider');
  }

  if (selector) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useStore(store, selector) as T;
  } else {
    return store.getState() as T;
  }
}

export { PhrStoreProvider, usePhrStoreInContext };
